import React, { useState, useEffect } from 'react';
import { TargetSelect } from './TargetSelect';
import AgentLossMapping from './AgentLossMapping';
import { atlasTechniqueEs, atlasTacticEs, attackRefEs, owaspLlmEs } from "../i18n/frameworksEs";

const AIBotSecurityScanner = () => {
  const [targetUrl, setTargetUrl] = useState('');
  const [botUrl, setBotUrl] = useState('');
  const [botType, setBotType] = useState('generic');
  const [scanning, setScanning] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [discoveryResults, setDiscoveryResults] = useState(null);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('form');
  const [scanMode, setScanMode] = useState('discover'); // 'discover' o 'direct'
  const [scans, setScans] = useState([]);
  const [selectedScan, setSelectedScan] = useState(null);

  useEffect(() => {
    fetchScans();
  }, []);

  const fetchScans = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/scans/ai-bot-security', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setScans(data);
      }
    } catch (err) {
      console.error('Error fetching AI bot scans:', err);
    }
  };

  const handleDiscover = async () => {
    if (!targetUrl) {
      setError('Por favor ingrese la URL del sitio web');
      return;
    }

    setDiscovering(true);
    setError(null);
    setDiscoveryResults(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/scans/discover-chatbots', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ target_url: targetUrl })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error en descubrimiento');
      }

      const data = await response.json();
      setDiscoveryResults(data.results);
      
      // Si encontró un endpoint recomendado, usarlo
      if (data.results.recommended_endpoint) {
        setBotUrl(data.results.recommended_endpoint);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setDiscovering(false);
    }
  };

  const handleScan = async () => {
    const urlToScan = scanMode === 'discover' ? botUrl : botUrl;
    
    if (!urlToScan) {
      setError('Por favor ingrese o seleccione la URL del endpoint del bot');
      return;
    }

    setScanning(true);
    setError(null);
    setResults(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/scans/ai-bot-security', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ bot_url: urlToScan, bot_type: botType })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error ejecutando escaneo');
      }

      const data = await response.json();
      setResults(data.results);
      setViewMode('results');
      fetchScans();
    } catch (err) {
      setError(err.message);
    } finally {
      setScanning(false);
    }
  };

  const viewScanDetails = (scan) => {
    setSelectedScan(scan);
    setResults(scan.results);
    setViewMode('results');
  };

  const getRiskColor = (score) => {
    if (score >= 80) return { bg: 'bg-green-50', border: 'border-green-500', text: 'text-green-700', badge: 'bg-green-500' };
    if (score >= 60) return { bg: 'bg-yellow-50', border: 'border-yellow-500', text: 'text-yellow-700', badge: 'bg-yellow-500' };
    if (score >= 40) return { bg: 'bg-orange-50', border: 'border-orange-500', text: 'text-orange-700', badge: 'bg-orange-500' };
    return { bg: 'bg-red-50', border: 'border-red-500', text: 'text-red-700', badge: 'bg-red-500' };
  };

  const getRiskLabel = (riskLevel) => {
    const labels = {
      'LOW': '✅ BAJO RIESGO',
      'MEDIUM': '⚠️ RIESGO MEDIO',
      'HIGH': '🔴 ALTO RIESGO',
      'CRITICAL': '🚨 CRÍTICO'
    };
    return labels[riskLevel] || riskLevel;
  };

  const getSeverityColor = (severity) => {
    const colors = {
      'CRITICAL': 'bg-red-100 text-red-800 border-red-300',
      'HIGH': 'bg-orange-100 text-orange-800 border-orange-300',
      'MEDIUM': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'LOW': 'bg-blue-100 text-blue-800 border-blue-300',
      'INFO': 'bg-gray-100 text-gray-800 border-gray-300'
    };
    return colors[severity] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getComplianceColor = (status) => {
    return status === 'PASSED' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          🤖 Scanner de Seguridad de Bots IA
        </h1>
        <p className="text-lg text-gray-600">
          Análisis de vulnerabilidades en Chatbots y Agentes de Inteligencia Artificial
        </p>
      </div>

      {/* View Mode Tabs */}
      <div className="mb-6 flex gap-4">
        <button
          onClick={() => setViewMode('form')}
          className={`px-6 py-3 rounded-lg font-semibold transition-all ${
            viewMode === 'form' ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          🚀 Nuevo Escaneo
        </button>
        <button
          onClick={() => setViewMode('history')}
          className={`px-6 py-3 rounded-lg font-semibold transition-all ${
            viewMode === 'history' ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          📋 Historial ({scans.length})
        </button>
      </div>

      {/* Scan Form */}
      {viewMode === 'form' && (
        <div className="space-y-6">
          {/* Scan Mode Selection */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Modo de Escaneo</h3>
            <div className="flex gap-4">
              <button
                onClick={() => setScanMode('discover')}
                className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                  scanMode === 'discover' 
                    ? 'border-indigo-500 bg-indigo-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="text-2xl mb-2">🔍</div>
                <div className="font-semibold text-gray-900">Descubrir Chatbots</div>
                <div className="text-sm text-gray-500">Buscar chatbots automáticamente en un sitio web</div>
              </button>
              <button
                onClick={() => setScanMode('direct')}
                className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                  scanMode === 'direct' 
                    ? 'border-indigo-500 bg-indigo-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="text-2xl mb-2">🎯</div>
                <div className="font-semibold text-gray-900">Endpoint Directo</div>
                <div className="text-sm text-gray-500">Ya conozco la URL del endpoint del chatbot</div>
              </button>
            </div>
          </div>

          {/* Discovery Mode */}
          {scanMode === 'discover' && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                🔍 Paso 1: Descubrir Chatbots en el Sitio
              </h3>
              <div className="space-y-4">
                <div>
                  <TargetSelect
                    label="URL del Sitio Web"
                    value={targetUrl}
                    onChange={setTargetUrl}
                    types={['domain']}
                    asUrl
                    disabled={discovering}
                    helpText="Solo dominios con autorización de escaneo vigente. El endpoint exacto del bot se completa en el paso siguiente."
                    testid="aibot-target-select"
                  />
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={handleDiscover}
                      disabled={discovering || !targetUrl}
                      className="px-6 py-3 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                    >
                      {discovering ? '🔍 Buscando...' : '🔍 Descubrir'}
                    </button>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Buscaremos widgets de chat (Intercom, Drift, Zendesk, etc.) y endpoints de API
                  </p>
                </div>

                {/* Discovery Results */}
                {discoveryResults && (
                  <div className={`p-4 rounded-lg border ${discoveryResults.bots_found ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
                    <h4 className="font-semibold mb-2">
                      {discoveryResults.bots_found ? '✅ Chatbots Encontrados' : '⚠️ No se encontraron chatbots'}
                    </h4>
                    
                    {discoveryResults.chatbot_widgets?.length > 0 && (
                      <div className="mb-3">
                        <p className="text-sm font-medium text-gray-700">Widgets detectados:</p>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {discoveryResults.chatbot_widgets.map((widget, i) => (
                            <span key={i} className="px-2 py-1 bg-white rounded text-sm border">
                              {widget.type}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {discoveryResults.api_endpoints?.length > 0 && (
                      <div className="mb-3">
                        <p className="text-sm font-medium text-gray-700">Endpoints de API encontrados:</p>
                        <div className="space-y-1 mt-1">
                          {discoveryResults.api_endpoints.map((ep, i) => (
                            <button
                              key={i}
                              onClick={() => setBotUrl(ep.url)}
                              className={`block w-full text-left px-3 py-2 rounded text-sm ${
                                botUrl === ep.url ? 'bg-indigo-100 border-indigo-300' : 'bg-white hover:bg-gray-50'
                              } border`}
                            >
                              <span className={ep.responds_to_chat ? 'text-green-600' : 'text-gray-600'}>
                                {ep.responds_to_chat ? '✓' : '○'}
                              </span>
                              {' '}{ep.url}
                              <span className="text-gray-400 ml-2">({ep.method} {ep.status_code})</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {discoveryResults.recommended_endpoint && (
                      <p className="text-sm text-green-700">
                        <strong>Recomendado:</strong> {discoveryResults.recommended_endpoint}
                      </p>
                    )}
                  </div>
                )}

                {/* Selected Endpoint */}
                {botUrl && (
                  <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                    <h4 className="font-semibold text-indigo-900 mb-2">
                      🎯 Paso 2: Escanear Endpoint Seleccionado
                    </h4>
                    <p className="text-sm text-indigo-700 mb-3">
                      Endpoint: <code className="bg-indigo-100 px-2 py-0.5 rounded">{botUrl}</code>
                    </p>
                    <button
                      onClick={handleScan}
                      disabled={scanning}
                      className="w-full px-6 py-3 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                    >
                      {scanning ? '⏳ Analizando Bot...' : '🔍 Iniciar Análisis de Seguridad'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Direct Mode */}
          {scanMode === 'direct' && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                🎯 Escaneo Directo de Endpoint
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    URL del Endpoint del Bot/Chatbot *
                  </label>
                  <input
                    type="text"
                    value={botUrl}
                    onChange={(e) => setBotUrl(e.target.value)}
                    placeholder="https://api.ejemplo.com/chatbot o https://ejemplo.com/api/chat"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    disabled={scanning}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de Bot
                  </label>
                  <select
                    value={botType}
                    onChange={(e) => setBotType(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    disabled={scanning}
                  >
                    <option value="generic">Genérico / Desconocido</option>
                    <option value="openai">OpenAI (GPT)</option>
                    <option value="anthropic">Anthropic (Claude)</option>
                    <option value="google">Google (Gemini/Bard)</option>
                    <option value="custom">Custom / Propio</option>
                  </select>
                </div>

                <button
                  onClick={handleScan}
                  disabled={scanning || !botUrl}
                  className="w-full px-8 py-4 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-lg"
                >
                  {scanning ? '⏳ Analizando Bot...' : '🔍 Iniciar Análisis de Seguridad'}
                </button>
              </div>
            </div>
          )}

          {/* Info Banner */}
          <div className="bg-indigo-50 border-l-4 border-indigo-500 p-6 rounded-lg">
            <div className="flex items-start">
              <span className="text-3xl mr-4">🛡️</span>
              <div>
                <h3 className="text-lg font-semibold text-indigo-900 mb-2">Vectores de Ataque Analizados</h3>
                <ul className="text-indigo-800 text-sm space-y-1 grid grid-cols-2 gap-x-4">
                  <li>• <strong>Inyección de Prompt</strong></li>
                  <li>• <strong>Ofuscación Unicode / Contrabando con Emojis</strong></li>
                  <li>• <strong>Fuga de Datos</strong></li>
                  <li>• <strong>Evasión de Restricciones (Jailbreak)</strong></li>
                  <li>• <strong>Extracción del Prompt de Sistema</strong></li>
                  <li>• <strong>Abuso de Funciones / API</strong></li>
                  <li>• <strong>Exposición de Datos de Entrenamiento</strong></li>
                  <li>• <strong>Límite de Tasa de Peticiones</strong></li>
                  <li>• <strong>Evasión de Autenticación</strong></li>
                </ul>
              </div>
            </div>
          </div>

          {/* Demo Bot Link */}
          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h4 className="font-semibold text-green-800 mb-1">💡 ¿Querés probar el scanner?</h4>
            <p className="text-sm text-green-700">
              Tenemos un bot de demostración con vulnerabilidades intencionales. 
              Endpoint: <code className="bg-green-100 px-2 py-0.5 rounded">https://dev.seguria.ar/api/demo-bot/chat</code>
            </p>
          </div>

          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              ❌ {error}
            </div>
          )}
        </div>
      )}

      {/* Loading State */}
      {scanning && (
        <div className="bg-white rounded-lg shadow-lg p-12 text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-xl font-semibold text-gray-900">Analizando seguridad del Bot IA...</p>
          <p className="text-gray-600 mt-2">Esto puede tomar 2-5 minutos</p>
        </div>
      )}

      {/* History View */}
      {viewMode === 'history' && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Historial de Escaneos</h3>
          {scans.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No hay escaneos registrados.</p>
              <button onClick={() => setViewMode('form')} className="mt-4 text-indigo-600 hover:underline">
                Crear primer escaneo →
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {scans.map((scan) => {
                const botScore = scan.results?.ai_bot_security_score || 0;
                const riskLevel = scan.results?.risk_level || 'UNKNOWN';
                return (
                  <div
                    key={scan.id}
                    className="border border-gray-200 rounded-lg p-4 hover:border-indigo-400 cursor-pointer transition"
                    onClick={() => viewScanDetails(scan)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900">{scan.target}</h4>
                        <p className="text-sm text-gray-500">
                          {new Date(scan.created_at).toLocaleString('es-AR')}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className={`text-2xl font-bold ${getRiskColor(botScore).text}`}>
                          {botScore}/100
                        </div>
                        <p className={`text-xs px-2 py-1 rounded ${getRiskColor(botScore).bg}`}>
                          {getRiskLabel(riskLevel)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Results View */}
      {viewMode === 'results' && results && (
        <div className="space-y-8">
          <button onClick={() => setViewMode('history')} className="text-indigo-600 hover:text-indigo-800 font-semibold">
            ← Volver al historial
          </button>

          {/* Score Principal */}
          <div className={`rounded-lg shadow-xl p-8 border-2 ${getRiskColor(results.ai_bot_security_score).border} ${getRiskColor(results.ai_bot_security_score).bg}`}>
            <div className="text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Seguridad de Bot IA</h2>
              <div className={`text-9xl font-bold mb-4 ${getRiskColor(results.ai_bot_security_score).text}`}>
                {results.ai_bot_security_score}
                <span className="text-4xl text-gray-500">/100</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-8 mb-6">
                <div
                  className={`h-8 rounded-full transition-all duration-500 ${getRiskColor(results.ai_bot_security_score).badge}`}
                  style={{ width: `${results.ai_bot_security_score}%` }}
                ></div>
              </div>
              <div className={`inline-block px-8 py-4 rounded-full font-bold text-2xl ${getRiskColor(results.ai_bot_security_score).badge} text-white shadow-lg`}>
                {getRiskLabel(results.risk_level)}
              </div>
              <div className="mt-6 grid grid-cols-3 gap-4">
                <div className="bg-white/50 rounded-lg p-3">
                  <div className="text-2xl font-bold text-gray-800">{results.total_tests_run}</div>
                  <div className="text-sm text-gray-600">Tests</div>
                </div>
                <div className="bg-white/50 rounded-lg p-3">
                  <div className="text-2xl font-bold text-gray-800">{results.vulnerabilities?.length || 0}</div>
                  <div className="text-sm text-gray-600">Vulnerabilidades</div>
                </div>
                <div className="bg-white/50 rounded-lg p-3">
                  <div className="text-2xl font-bold text-gray-800">{results.bot_type}</div>
                  <div className="text-sm text-gray-600">Tipo</div>
                </div>
              </div>
            </div>
          </div>

          {/* Compliance Summary */}
          {results.compliance_summary && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">✅ Cumplimiento de Seguridad</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(results.compliance_summary).map(([key, status]) => (
                  <div key={key} className={`p-4 rounded-lg border ${getComplianceColor(status)}`}>
                    <span className="text-sm font-medium capitalize">{key.replace(/_/g, ' ')}</span>
                    <span className="float-right font-bold">{status === 'PASSED' ? '✓' : '✗'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <AgentLossMapping botUrl={botUrl} />

          {/* Mapeo MITRE ATLAS */}
          {results.atlas_profile?.techniques?.length > 0 && (
            <div className="bg-white rounded-lg shadow-lg p-6" data-testid="atlas-profile-panel">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">🎯 Mapeo MITRE ATLAS</h3>
              <p className="text-sm text-gray-600 mb-5">
                ATLAS es el framework de MITRE específico para ataques a sistemas de IA. Se exponen{' '}
                <strong>{results.atlas_profile.total_techniques_exposed}</strong> técnicas en{' '}
                <strong>{Object.keys(results.atlas_profile.tactics_exposed || {}).length}</strong> tácticas.
              </p>

              <div className="flex flex-wrap gap-2 mb-5">
                {Object.entries(results.atlas_profile.tactics_exposed || {}).map(([tactic, count]) => (
                  <span
                    key={tactic}
                    data-testid={`atlas-tactic-${tactic.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`}
                    className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-900 text-white"
                  >
                    {atlasTacticEs(tactic)} · {count}
                  </span>
                ))}
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b">
                      <th className="py-2 pr-3">Técnica ATLAS</th>
                      <th className="py-2 pr-3">Táctica</th>
                      <th className="py-2 pr-3">ATT&CK</th>
                      <th className="py-2">OWASP LLM</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.atlas_profile.techniques.map((t, i) => (
                      <tr key={i} className="border-b last:border-0 align-top" data-testid={`atlas-technique-row-${i}`}>
                        <td className="py-3 pr-3">
                          <div className="font-mono text-xs text-indigo-700 font-bold">{t.atlas_technique_id}</div>
                          <div className="font-medium text-gray-900">{atlasTechniqueEs(t.atlas_technique_id, t.atlas_technique)}</div>
                          <div className="text-xs text-gray-500">{t.finding}</div>
                        </td>
                        <td className="py-3 pr-3 text-gray-700 text-xs">{atlasTacticEs(t.atlas_tactic)}</td>
                        <td className="py-3 pr-3 text-gray-700 text-xs">{attackRefEs(t.attack_technique)}</td>
                        <td className="py-3 text-gray-700 text-xs">{owaspLlmEs(t.owasp_llm)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Prompt Injection - Tecnicas evaluadas */}
          {results.detailed_results?.prompt_injection?.techniques_tested?.length > 0 && (
            <div className="bg-white rounded-lg shadow-lg p-6" data-testid="prompt-injection-techniques-panel">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">🧬 Prompt Injection — Técnicas Evaluadas</h3>
              <p className="text-sm text-gray-600 mb-5">
                Incluye variantes de <strong>ofuscación Unicode / Emoji Smuggling</strong>: el payload viaja
                escondido en caracteres invisibles en vez de texto plano visible.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {results.detailed_results.prompt_injection.techniques_tested.map((t, i) => (
                  <div
                    key={i}
                    data-testid={`injection-technique-${i}`}
                    className={`flex items-start justify-between gap-3 p-3 rounded-lg border ${
                      t.vulnerable ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'
                    }`}
                  >
                    <div className="min-w-0">
                      <div className="font-medium text-gray-900 text-sm">{t.name}</div>
                      <div className="text-xs text-gray-500 break-words">{t.technique}</div>
                      {t.evidence_level && t.evidence_level !== "NONE" && (
                        <span className="inline-block mt-1 mr-1 px-2 py-0.5 rounded text-[10px] font-bold bg-slate-700 text-white">
                          {t.evidence_level === "PROVEN" ? "DEMOSTRADA" : t.evidence_level === "OBSERVED" ? "OBSERVADA" : t.evidence_level}
                        </span>
                      )}
                      {t.obfuscation && (
                        <span className="inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-bold bg-indigo-100 text-indigo-700 border border-indigo-200">
                          OFUSCACIÓN UNICODE
                        </span>
                      )}
                    </div>
                    <span className={`shrink-0 px-2 py-1 rounded text-xs font-bold ${
                      t.vulnerable ? 'bg-red-600 text-white' : 'bg-green-600 text-white'
                    }`}>
                      {t.vulnerable ? 'VULNERABLE' : 'OK'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Vulnerabilities */}
          {results.vulnerabilities?.length > 0 && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">🛡️ Vulnerabilidades ({results.vulnerabilities.length})</h3>
              <div className="space-y-4">
                {results.vulnerabilities.map((vuln, index) => (
                  <div
                    key={index}
                    className={`border-l-4 rounded-lg p-4 ${
                      vuln.severity === 'CRITICAL' ? 'border-red-500 bg-red-50' :
                      vuln.severity === 'HIGH' ? 'border-orange-500 bg-orange-50' :
                      vuln.severity === 'MEDIUM' ? 'border-yellow-500 bg-yellow-50' :
                      'border-blue-500 bg-blue-50'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-semibold text-gray-900">{vuln.title}</h4>
                      <span className={`px-2 py-1 rounded text-xs font-bold border ${getSeverityColor(vuln.severity)}`}>
                        {vuln.severity}
                      </span>
                    </div>
                    {vuln.evidence_level && (
                      <div className="flex flex-wrap items-center gap-2 mb-2" data-testid={`vuln-evidence-${index}`}>
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold text-white ${
                          vuln.evidence_level === "PROVEN" ? "bg-red-700"
                          : vuln.evidence_level === "OBSERVED" ? "bg-amber-600" : "bg-slate-500"
                        }`}>
                          EVIDENCIA: {vuln.evidence_level === "PROVEN" ? "DEMOSTRADA"
                            : vuln.evidence_level === "OBSERVED" ? "OBSERVADA" : "INDICIO"}
                        </span>
                        {vuln.base_severity_before_evidence && vuln.base_severity_before_evidence !== vuln.severity && (
                          <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600 border border-slate-300">
                            severidad ajustada desde {vuln.base_severity_before_evidence} por nivel de evidencia
                          </span>
                        )}
                      </div>
                    )}
                    {vuln.evidence_detail && (
                      <p className="text-xs text-slate-500 italic mb-2">{vuln.evidence_detail}</p>
                    )}
                    {vuln.subcategory && (
                      <div className="mb-2">
                        <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-gray-800 text-white" data-testid={`vuln-subcategory-${index}`}>
                          {vuln.subcategory}
                        </span>
                      </div>
                    )}
                    {(vuln.atlas_technique_id || vuln.attack_technique) && (
                      <div className="flex flex-wrap gap-2 mb-3" data-testid={`vuln-frameworks-${index}`}>
                        {vuln.atlas_technique_id && (
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-indigo-600 text-white">
                            ATLAS {vuln.atlas_technique_id} · {atlasTechniqueEs(vuln.atlas_technique_id, vuln.atlas_technique)}
                          </span>
                        )}
                        {vuln.attack_technique && (
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-700 text-white">
                            ATT&CK {attackRefEs(vuln.attack_technique)}
                          </span>
                        )}
                        {vuln.owasp_category && (
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-orange-600 text-white">
                            OWASP {owaspLlmEs(vuln.owasp_category)}
                          </span>
                        )}
                      </div>
                    )}
                    <p className="text-sm text-gray-700 mb-3">{vuln.description}</p>
                    {vuln.ai_risk && (
                      <div className="mb-3 p-3 bg-red-100/50 rounded border border-red-200">
                        <p className="text-sm text-red-800"><strong>⚠️ Riesgo IA:</strong> {vuln.ai_risk}</p>
                      </div>
                    )}
                    {vuln.recommendation && (
                      <div className="p-3 bg-white/80 rounded border border-gray-200">
                        <p className="text-sm text-gray-600"><strong>💡 Recomendación:</strong> {vuln.recommendation}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommendations */}
          {results.recommendations?.length > 0 && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">💡 Recomendaciones</h3>
              <div className="space-y-4">
                {results.recommendations.map((rec, index) => (
                  <div key={index} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                      rec.severity === 'CRITICAL' ? 'bg-red-500' :
                      rec.severity === 'HIGH' ? 'bg-orange-500' :
                      rec.severity === 'MEDIUM' ? 'bg-yellow-500' : 'bg-blue-500'
                    }`}>
                      {rec.priority}
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{rec.title}</h4>
                      <p className="text-sm text-gray-600 mt-1">{rec.action}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AIBotSecurityScanner;
